import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axiosInstance from '../api/axiosInstance';
import { Container, Row, Col, Pagination, Spinner, Modal, Form } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaRegClock, FaSearch, FaBookOpen, FaVolumeUp, FaVolumeMute, FaPlay, FaShareAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import '../css/Wageundam.css'; // 와근담 전용 스타일시트

const ITEMS_PER_PAGE = 12;
const API_URL = "posts";
const BOARD_ID = 6; // 와근담 전용 보드 ID
// 책 페이지 분할 유틸리티 함수
const splitTextIntoPages = (text, maxLength = 350) => {
    if (!text) return [""];
    const paragraphs = text.split("\n");
    const pages = [];
    let currentPage = "";

    for (let i = 0; i < paragraphs.length; i++) {
        const para = paragraphs[i];
        
        // 문단 하나가 최대 글자 수보다 긴 경우 문장 단위로 분할
        if (para.length > maxLength) {
            const sentences = para.match(/[^.!?]+[.!?]+(\s|$)/g) || [para];
            for (let j = 0; j < sentences.length; j++) {
                const sentence = sentences[j];
                if (currentPage.length + sentence.length > maxLength && currentPage.trim().length > 0) {
                    pages.push(currentPage.trim());
                    currentPage = sentence;
                } else {
                    currentPage += sentence;
                }
            }
        } else {
            if (currentPage.length + para.length > maxLength && currentPage.trim().length > 0) {
                pages.push(currentPage.trim());
                currentPage = para + "\n";
            } else {
                currentPage += para + "\n";
            }
        }
    }
    
    if (currentPage.trim().length > 0) {
        pages.push(currentPage.trim());
    }
    
    return pages.length > 0 ? pages : [text];
};

// 책 페이지 전환 (3D 회전 및 슬라이드) 애니메이션 Variants
const pageTurnVariants = {
    enter: (direction) => ({
        x: direction > 0 ? "100%" : "-100%",
        rotateY: direction > 0 ? 45 : -45,
        opacity: 0,
    }),
    center: {
        x: 0,
        rotateY: 0,
        opacity: 1,
        transition: {
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
            rotateY: { duration: 0.3 }
        }
    },
    exit: (direction) => ({
        x: direction < 0 ? "100%" : "-100%",
        rotateY: direction < 0 ? 45 : -45,
        opacity: 0,
        transition: {
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
            rotateY: { duration: 0.3 }
        }
    })
};

const Wageundam = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // URL 쿼리 스트링에서 상태 동기화 (Source of Truth)
    const searchParams = new URLSearchParams(location.search);
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const searchTitle = searchParams.get('title') || "";

    const [postList, setPostList] = useState([]);
    const [titleInput, setTitleInput] = useState(searchTitle);
    const [totalPostCount, setTotalPostCount] = useState(0);
    const [loading, setLoading] = useState(false);
    
    // 모달 상세 보기 상태
    const [showModal, setShowModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isMuted, setIsMuted] = useState(true);
    const [videoProgress, setVideoProgress] = useState(0);
    const [volume, setVolume] = useState(0.5); // 초기 볼륨 50%
    const videoRef = useRef(null);

    // 책 페이지 뷰어 관련 상태
    const [currentPageIdx, setCurrentPageIdx] = useState(0);
    const [direction, setDirection] = useState(0); // -1: 이전, 1: 다음
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [toastMessage, setToastMessage] = useState("");

    // 창 크기 조절 감지
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 비디오 볼륨 동기화
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }
    }, [volume, selectedPost]);

    // 비디오 재생 시간 업데이트 핸들러
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const duration = videoRef.current.duration;
            if (duration) {
                setVideoProgress((current / duration) * 100);
            }
        }
    };

    // 오디오 토글 핸들러
    const toggleMute = (e) => {
        e.stopPropagation();
        setIsMuted((prev) => !prev);
    };

    // 비디오 영역 클릭 핸들러 (오디오 토글)
    const handleVideoClick = (e) => {
        e.stopPropagation();
        setIsMuted((prev) => !prev);
    };

    // 볼륨 조절 핸들러
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
        }
    };

    // API 호출 함수
    const fetchPosts = useCallback(async (page, title) => {
        setLoading(true);
        let url = `${API_URL}?board_id=${BOARD_ID}&page=${page}`;
        if (title) {
            url += `&title=${encodeURIComponent(title)}`;
        }

        try {
            const response = await axiosInstance.get(url);
            const apiData = response.data.data;

            if (apiData?.postList) {
                setPostList(apiData.postList);
                // 첫 번째 항목의 postcount 또는 기본 값 파싱
                const count = parseInt(apiData.postList[0]?.json?.postcount || apiData.postList[0]?.postcount || 0, 10);
                setTotalPostCount(count);
            } else {
                setPostList([]);
                setTotalPostCount(0);
            }
        } catch (error) {
            console.error("Error fetching Wageundam posts:", error);
            setPostList([]);
            setTotalPostCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    // URL 변경에 따른 데이터 동기화
    useEffect(() => {
        fetchPosts(currentPage, searchTitle);
        setTitleInput(searchTitle);
    }, [fetchPosts, currentPage, searchTitle]);

    // 토스트 알림 제어 함수
    const showToastMessage = (msg) => {
        setToastMessage(msg);
        setTimeout(() => {
            setToastMessage("");
        }, 2000);
    };

    // 링크 공유 처리 함수
    const handleShareClick = (e, post) => {
        e.stopPropagation();
        
        // 현재 브라우저의 origin과 pathname을 유지하여 환경 독립적인 URL 생성
        const newParams = new URLSearchParams(location.search);
        newParams.set("postId", post.id);
        const shareUrl = `${window.location.origin}${window.location.pathname}?${newParams.toString()}`;
        
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                showToastMessage("링크가 복사되었습니다!");
            })
            .catch((err) => {
                console.error("복사 실패:", err);
                // 모바일 구형 브라우저 대응 fallback
                const textArea = document.createElement("textarea");
                textArea.value = shareUrl;
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand("copy");
                    showToastMessage("링크가 복사되었습니다!");
                } catch (fallbackErr) {
                    console.error("Fallback 복사 실패:", fallbackErr);
                }
                document.body.removeChild(textArea);
            });
    };

    // URL에 postId 쿼리 매개변수가 있는지 감지하여 상세 모달 자동 노출
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlPostId = params.get("postId");
        
        if (urlPostId) {
            const parsedId = parseInt(urlPostId, 10);
            
            // 이미 모달이 열려 있고 해당 ID와 일치하면 중복 실행 방지
            if (selectedPost && selectedPost.id === parsedId) {
                return;
            }
            
            // 1. 현재 불러와진 목록에서 해당 포스트 탐색
            const existingPost = postList.find(p => {
                const item = p.json || p;
                return item.id === parsedId;
            });
            
            if (existingPost) {
                const item = existingPost.json || existingPost;
                setSelectedPost(item);
                setCurrentPageIdx(0);
                setDirection(0);
                setShowModal(true);
            } else {
                // 2. 목록에 없는 경우 API를 호출하여 단일 포스트 획득 (제공된 JSON 구조 파싱)
                const fetchSinglePost = async () => {
                    try {
                        const response = await axiosInstance.get(`postDetail?post_id=${parsedId}`);
                        const apiData = response.data.data;
                        if (apiData?.postList && apiData.postList.length > 0) {
                            const postObj = apiData.postList[0];
                            const item = postObj.json || postObj;
                            setSelectedPost(item);
                            setCurrentPageIdx(0);
                            setDirection(0);
                            setShowModal(true);
                        }
                    } catch (error) {
                        console.error("Error fetching single post details:", error);
                    }
                };
                fetchSinglePost();
            }
        }
    }, [location.search, postList, selectedPost]);

    // 카드 클릭 시 모달 열기
    const handleCardClick = (post) => {
        setSelectedPost(post);
        setCurrentPageIdx(0);
        setDirection(0);
        setShowModal(true);
        
        // URL에 postId 반영 (페이지 및 검색 조건 보존)
        const newParams = new URLSearchParams(location.search);
        newParams.set("postId", post.id);
        navigate({ search: newParams.toString() }, { replace: true });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPost(null);
        setIsMuted(true);
        setVideoProgress(0);
        setVolume(0.5);
        setCurrentPageIdx(0);
        setDirection(0);
        
        // URL에서 postId 제거 (페이지 및 검색 조건 보존)
        const newParams = new URLSearchParams(location.search);
        newParams.delete("postId");
        navigate({ search: newParams.toString() }, { replace: true });
    };

    // 소설 내용을 책 페이지 형태로 분할
    const pages = useMemo(() => {
        if (!selectedPost) return [""];
        const content = selectedPost.content ? selectedPost.content.replace(/\\n/g, "\n") : "";
        const isMobile = windowWidth <= 768;
        const maxLength = isMobile ? 140 : 220;
        return splitTextIntoPages(content, maxLength);
    }, [selectedPost, windowWidth]);

    const handleNextPage = useCallback(() => {
        if (currentPageIdx < pages.length - 1) {
            setDirection(1);
            setCurrentPageIdx((prev) => prev + 1);
        }
    }, [currentPageIdx, pages.length]);

    const handlePrevPage = useCallback(() => {
        if (currentPageIdx > 0) {
            setDirection(-1);
            setCurrentPageIdx((prev) => prev - 1);
        }
    }, [currentPageIdx]);

    // 키보드 방향키로 페이지 전환
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showModal) return;
            if (e.key === "ArrowRight") {
                handleNextPage();
            } else if (e.key === "ArrowLeft") {
                handlePrevPage();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [showModal, handleNextPage, handlePrevPage]);

    // 모바일/PC 스와이프 및 드래그 제스처 감지
    const handleDragEnd = (event, info) => {
        const swipeThreshold = 50;
        if (info.offset.x < -swipeThreshold) {
            handleNextPage();
        } else if (info.offset.x > swipeThreshold) {
            handlePrevPage();
        }
    };

    // 페이지네이션 처리
    const totalPages = Math.ceil(totalPostCount / ITEMS_PER_PAGE);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            const newParams = new URLSearchParams(location.search);
            newParams.set('page', page);
            navigate({ search: newParams.toString() }, { replace: true });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // 검색 처리
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const newParams = new URLSearchParams();
        newParams.set('page', 1);
        if (titleInput.trim()) {
            newParams.set('title', titleInput.trim());
        }
        navigate({ search: newParams.toString() }, { replace: true });
    };

    // Framer Motion 카드 애니메이션 효과
    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: (idx) => ({
            opacity: 1, 
            y: 0,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 15,
                delay: idx * 0.08
            }
        })
    };

    return (
        <Container className="py-5 wageundam-container">
            {/* 타이틀 헤더 */}
            <div className="wg-header mb-5">
                <h1 className="wg-title">와근담 (蛙根談)</h1>
                <p className="wg-subtitle">AI가 쓰고 그린 짧은 웹소설과 감성 삽화의 조화</p>
            </div>

            {/* 미니멀 검색 창 */}
            <Form onSubmit={handleSearchSubmit} className="mb-5">
                <Row className="justify-content-center">
                    <Col xs={12} md={6} lg={4}>
                        <div className="d-flex align-items-center bg-white border rounded-pill px-3 py-2 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
                            <FaSearch className="text-muted me-2" />
                            <Form.Control
                                type="text"
                                placeholder="소설 제목 검색..."
                                value={titleInput}
                                onChange={(e) => setTitleInput(e.target.value)}
                                className="border-0 p-0 bg-transparent focus-none"
                                style={{ boxShadow: 'none', fontSize: '0.95rem' }}
                            />
                        </div>
                    </Col>
                </Row>
            </Form>

            {/* 로딩 표시 */}
            {loading && (
                <div className="text-center my-5 py-5">
                    <Spinner animation="border" variant="secondary" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-secondary" style={{ fontFamily: 'Nanum Myeongjo', fontSize: '0.95rem' }}>책장에서 이야기를 꺼내는 중...</p>
                </div>
            )}

            {/* 게시글 리스트 */}
            {!loading && postList.length > 0 && (
                <div className="wg-grid">
                    {postList.map((post, index) => {
                        const item = post.json || post;
                        const title = item.title || "";
                        const content = item.content ? item.content.replace(/\\n/g, "\n") : "";
                        const imageUrl = item.extra_data?.imageUrl || "";
                        const date = item.created_at ? item.created_at.substring(0, 10) : "";

                        return (
                            <motion.div
                                key={item.id || index}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                variants={cardVariants}
                                className="wg-card-wrapper"
                            >
                                <div className="wg-card" onClick={() => handleCardClick(item)}>
                                    {/* 빨간 북마크 리본 */}
                                    <div className="wg-ribbon"></div>
                                    
                                    {/* 좌측 이미지 영역 */}
                                    <div className="wg-card-img-area">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={title} className="wg-card-img" loading="lazy" />
                                        ) : (
                                            <div className="d-flex h-100 align-items-center justify-content-center text-muted bg-light">
                                                <FaBookOpen size={30} />
                                            </div>
                                        )}
                                        {item.extra_data?.videoUrl && (
                                            <div className="wg-video-badge">
                                                <FaPlay className="wg-video-badge-icon" />
                                                <span>Video</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* 우측 텍스트 영역 */}
                                    <div className="wg-card-content">
                                        <div>
                                            <h3 className="wg-card-title">{title}</h3>
                                            <p className="wg-card-text">{content}</p>
                                        </div>
                                        
                                        <div className="wg-card-footer">
                                            <span className="wg-card-date">
                                                <FaRegClock size={12} className="me-1" />
                                                {date}
                                            </span>
                                            <div className="d-flex align-items-center gap-3">
                                                <button
                                                    className="wg-card-share-btn"
                                                    onClick={(e) => handleShareClick(e, item)}
                                                    title="공유 링크 복사"
                                                >
                                                    <FaShareAlt size={12} />
                                                </button>
                                                <span className="wg-card-more">Read Story →</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* 검색 결과 없음 */}
            {!loading && postList.length === 0 && (
                <div className="text-center my-5 py-5 bg-light rounded-4 shadow-sm" style={{ border: '1px dashed #cbd5e1' }}>
                    <FaBookOpen size={45} className="text-muted mb-3" />
                    <h4 className="text-secondary mb-2" style={{ fontFamily: 'Nanum Myeongjo', fontWeight: 'bold' }}>아직 쓰이지 않은 장입니다.</h4>
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>검색어를 바꾸거나 목록을 새로고침해 주세요.</p>
                </div>
            )}

            {/* 페이지네이션 */}
            {!loading && totalPages > 1 && (
                <div className="wg-pagination-wrapper">
                    <Pagination className="wg-pagination">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                        {[...Array(totalPages)].map((_, i) => {
                            const pageNum = i + 1;
                            const startPage = Math.max(1, currentPage - 2);
                            const endPage = Math.min(totalPages, startPage + 4);

                            if (pageNum >= startPage && pageNum <= endPage) {
                                return (
                                    <Pagination.Item
                                        key={pageNum}
                                        active={pageNum === currentPage}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </Pagination.Item>
                                );
                            }
                            return null;
                        })}

                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}

            {/* 상세 보기 책 뷰어 모달 */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                centered
                dialogClassName="wg-modal-dialog"
                contentClassName="wg-modal-content"
            >
                {selectedPost && (
                    <div className="wg-modal-body">
                        {/* 닫기 버튼 */}
                        <button className="wg-modal-close-btn" onClick={handleCloseModal}>×</button>

                        {/* 왼쪽: 이미지/비디오 미디어 컬럼 */}
                        <div 
                            className="wg-modal-img-column wg-modal-media-column"
                            onClick={selectedPost.extra_data?.videoUrl ? handleVideoClick : undefined}
                            style={{ cursor: selectedPost.extra_data?.videoUrl ? 'pointer' : 'default' }}
                        >
                            {selectedPost.extra_data?.videoUrl ? (
                                <div className="wg-modal-video-wrapper">
                                    <video
                                        ref={videoRef}
                                        src={selectedPost.extra_data.videoUrl}
                                        poster={selectedPost.extra_data.imageUrl}
                                        className="wg-modal-video"
                                        autoPlay
                                        loop
                                        muted={isMuted}
                                        playsInline
                                        onTimeUpdate={handleTimeUpdate}
                                    />
                                    
                                    {/* 오디오 토글 안내 배지 */}
                                    <div 
                                        className={`wg-video-sound-badge ${isMuted ? 'is-muted' : 'is-unmuted'}`} 
                                        onClick={toggleMute}
                                        title={isMuted ? "소리 켜기" : "소리 끄기"}
                                    >
                                        {isMuted ? (
                                            <FaVolumeMute className="wg-sound-icon pulse-icon" />
                                        ) : (
                                            <FaVolumeUp className="wg-sound-icon" />
                                        )}
                                    </div>

                                    {/* 볼륨 게이지 (소리가 켜져 있을 때만 표시) */}
                                    {!isMuted && (
                                        <div className="wg-video-volume-slider-wrapper" onClick={(e) => e.stopPropagation()}>
                                            <input 
                                                type="range" 
                                                min="0" 
                                                max="1" 
                                                step="0.05" 
                                                value={volume} 
                                                onChange={handleVolumeChange} 
                                                className="wg-video-volume-slider"
                                                title={`볼륨: ${Math.round(volume * 100)}%`}
                                            />
                                        </div>
                                    )}

                                    {/* 재생 진행 상태 바 */}
                                    <div className="wg-video-progress-bar">
                                        <div 
                                            className="wg-video-progress-fill" 
                                            style={{ width: `${videoProgress}%` }}
                                        />
                                    </div>
                                </div>
                            ) : selectedPost.extra_data?.imageUrl ? (
                                <img src={selectedPost.extra_data.imageUrl} alt={selectedPost.title} className="wg-modal-img" />
                            ) : (
                                <div className="d-flex h-100 align-items-center justify-content-center text-muted bg-dark">
                                    <FaBookOpen size={50} />
                                </div>
                            )}
                            {/* 중앙 접히는 입체 섀도우 효과 */}
                            <div className="wg-modal-spine-shadow"></div>
                        </div>

                        {/* 오른쪽: 소설 줄거리 텍스트 컬럼 */}
                        <div className="wg-modal-text-column">
                            <h2 className="wg-modal-title d-flex justify-content-between align-items-center">
                                <span>{selectedPost.title}</span>
                                <button 
                                    className="wg-modal-share-btn"
                                    onClick={(e) => handleShareClick(e, selectedPost)}
                                    title="공유 링크 복사"
                                >
                                    <FaShareAlt size={14} />
                                </button>
                            </h2>
                            
                            {/* 책 페이지 컨테이너 */}
                            <div className="wg-book-page-container">
                                {/* 이전 페이지 이동 버튼 (데스크톱 마우스오버 화살표) */}
                                {currentPageIdx > 0 && (
                                    <button 
                                        className="wg-page-arrow-btn left" 
                                        onClick={handlePrevPage}
                                        title="이전 페이지 (←)"
                                    >
                                        ‹
                                    </button>
                                )}
                                
                                <AnimatePresence initial={false} custom={direction} mode="wait">
                                    <motion.div
                                        key={currentPageIdx}
                                        custom={direction}
                                        variants={pageTurnVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        drag="x"
                                        dragConstraints={{ left: 0, right: 0 }}
                                        dragElastic={0.4}
                                        onDragEnd={handleDragEnd}
                                        className="wg-book-page"
                                    >
                                        <div className="wg-modal-story-content">
                                            {pages[currentPageIdx]}
                                        </div>
                                    </motion.div>
                                </AnimatePresence>

                                {/* 다음 페이지 이동 버튼 (데스크톱 마우스오버 화살표) */}
                                {currentPageIdx < pages.length - 1 && (
                                    <button 
                                        className="wg-page-arrow-btn right" 
                                        onClick={handleNextPage}
                                        title="다음 페이지 (→)"
                                    >
                                        ›
                                    </button>
                                )}
                            </div>

                            {/* 페이지 하단 조작기 및 진행도 */}
                            <div className="wg-book-controls">
                                <button 
                                    className="wg-page-btn prev"
                                    onClick={handlePrevPage}
                                    disabled={currentPageIdx === 0}
                                >
                                    이전
                                </button>
                                <div className="wg-page-indicator">
                                    <span>{currentPageIdx + 1}</span> / <span>{pages.length}</span>
                                </div>
                                <button 
                                    className="wg-page-btn next"
                                    onClick={handleNextPage}
                                    disabled={currentPageIdx === pages.length - 1}
                                >
                                    다음
                                </button>
                            </div>

                            <div className="wg-modal-footer">
                                <span>
                                    <FaRegClock size={12} className="me-1" />
                                    작성일: {selectedPost.created_at ? selectedPost.created_at.substring(0, 10) : ""}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* 공유 완료 토스트 알림 */}
            <AnimatePresence>
                {toastMessage && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        className="wg-toast-notification"
                    >
                        <FaShareAlt className="me-2 text-warning" />
                        {toastMessage}
                    </motion.div>
                )}
            </AnimatePresence>
        </Container>
    );
};

export { Wageundam };
export default Wageundam;
